import {
  PresenceUser,
  PresenceMessage,
  PresenceTreeNode,
} from "../hooks/presenceTypes";

export type PresenceState = {
  users: PresenceUser[];
};

export const initialPresenceState: PresenceState = {
  users: [],
};

function flattenTree(node: PresenceTreeNode): PresenceUser[] {
  const rootUsers = (node.users ?? []).map((u) => ({
    ...u,
    slugs: [node.slug],
  }));
  const childUsers = Object.values(node.children ?? {}).flatMap(flattenTree);
  return [...rootUsers, ...childUsers];
}

export function presenceReducer(
  state: PresenceState,
  action: PresenceMessage
): PresenceState {
  switch (action.action) {
    case "snapshot": {
      // flattenTree produces one entry per user-per-node, so merge them
      // This handles edge case of user A viewing resource from separate instances
      const flat = flattenTree(action.tree);
      const merged = new Map<number, PresenceUser>();
      for (const user of flat) {
        if (merged.has(user.id)) {
          const existing = merged.get(user.id)!;
          merged.set(user.id, {
            ...existing,
            slugs: [...new Set([...existing.slugs, ...user.slugs])],
          });
        } else {
          merged.set(user.id, user);
        }
      }
      return { ...state, users: Array.from(merged.values()) };
    }
    case "join": {
      if (!action.user?.id) return state;
      const existing = state.users.find((u) => u.id === action.user.id);
      if (existing) {
        // user already present — add the new slug if not already tracked
        return {
          ...state,
          users: state.users.map((u) =>
            u.id === action.user.id
              ? { ...u, slugs: [...new Set([...u.slugs, action.slug])] }
              : u
          ),
        };
      }
      return {
        ...state,
        users: [
          ...state.users,
          {
            id: action.user.id,
            first_name: action.user.first_name ?? "",
            last_name: action.user.last_name ?? "",
            tab_count: action.user.tab_count,
            slugs: [action.slug],
          },
        ],
      };
    }
    case "evict": {
      if (!action.user?.id) return state;
      return {
        ...state,
        users: state.users
          .map((u) =>
            u.id === action.user.id
              ? { ...u, slugs: u.slugs.filter((s) => s !== action.slug) }
              : u
          )
          .filter((u) => u.id !== action.user.id || u.slugs.length > 0),
      };
    }
    case "reset":
      return initialPresenceState;
    default:
      return state;
  }
}
